// src/components/SimpleGameTimeOccupancyChart.js
import React, {useState} from 'react';
import {
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
    Line, ComposedChart,
} from 'recharts';
import {Typography, Switch, FormControlLabel} from '@mui/material';

// Helper: Format a given date (YYYY-MM-DD) and hour into "DD/M hh:mm AM/PM"
const formatHourLabel = (dateStr, hour) => {
    const dateTimeStr = `${dateStr}T${hour.toString().padStart(2, '0')}:00:00Z`;
    const d = new Date(dateTimeStr);
    if (isNaN(d)) return '';
    const options = {day: 'numeric', month: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true};
    return d.toLocaleString('en-US', options);
};

// Helper: Format a given date (YYYY-MM-DD) into "DD/M"
const formatDayLabel = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return '';
    const day = d.getUTCDate();
    const month = d.getUTCMonth() + 1;
    return `${day}/${month}`;
};

// Helper: Determine if a given date (YYYY-MM-DD) is a weekend.
const isWeekend = (dateStr) => {
    const d = new Date(dateStr);
    const day = d.getUTCDay();
    return day === 0 || day === 6;
};

// Compute effective seconds for a session WITHOUT multiplying by the number of players.
// Only if there is at least one player; otherwise return 0.
const computeEffectiveSecondsNoPlayers = (item) => {
    const players = Number(item.attributes.score) || 0;
    if (players < 1) return 0;
    const gameStart = new Date(item.attributes.gameStartDate);
    const gameEnd = new Date(item.attributes.gameEndDate);
    if (isNaN(gameStart) || isNaN(gameEnd)) return 0;
    const actual = (gameEnd - gameStart) / 1000;
    const planned = item.attributes.game_variant &&
    item.attributes.game_variant.data &&
    item.attributes.game_variant.data.attributes &&
    item.attributes.game_variant.data.attributes.duration
        ? item.attributes.game_variant.data.attributes.duration
        : actual;
    return planned ? Math.min(actual, planned) : actual;
};

// Unified function: groups rawData by hour (if grouping === 'hour') or by day (if grouping === 'day').
// For "hour": groups by date and hour (only sessions within working period) and capacity = 3600 seconds.
// For "day": groups by date (only sessions within working period) and capacity = (workingEnd – workingStart) * 3600.
const calculateSimpleOccupiedData = (rawData, grouping, workingStart, workingEnd) => {
    const groups = {};
    rawData.forEach(item => {
        const startStr = item.attributes.gameStartDate;
        const endStr = item.attributes.gameEndDate;
        if (!startStr || !endStr) return;
        const d = new Date(startStr);
        if (isNaN(d)) return;
        const date = d.toISOString().split('T')[0]; // "YYYY-MM-DD"
        const hour = d.getUTCHours();
        // Only consider sessions within working period.
        if (hour < workingStart || hour >= workingEnd) return;
        // For grouping, key is either "YYYY-MM-DD_hour" or just "YYYY-MM-DD".
        const key = grouping === 'hour' ? `${date}_${hour}` : date;
        if (!groups[key]) {
            groups[key] = {
                date,
                hours: grouping === 'hour' ? [hour] : [hour],
                effectiveTotal: 0,
                totalPlayers: 0
            };
        }
        groups[key].effectiveTotal += computeEffectiveSecondsNoPlayers(item);
        const players = Number(item.attributes.score) || 0;
        groups[key].totalPlayers += players;
        if (grouping === 'day') {
            groups[key].hours.push(hour);
        }
    });
    const chartData = [];
    Object.values(groups).forEach(group => {
        let capacity;
        let label;
        if (group.hours.length === 0) return;
        if (group.hours.length === 1) {
            // Hourly grouping: capacity = 3600 seconds.
            capacity = 3600;
            label = formatHourLabel(group.date, group.hours[0]);
        } else {
            // Daily grouping: capacity = (workingEnd - workingStart) * 3600.
            const workingHours = workingEnd - workingStart;
            capacity = workingHours * 3600;
            label = formatDayLabel(group.date);
        }
        const occupancy = capacity > 0 ? (group.effectiveTotal / capacity) * 100 : 0;
        chartData.push({
            label,
            occupancy: +occupancy.toFixed(2),
            totalPlayers: group.totalPlayers,
            date: group.date,
            hour: grouping === 'hour' ? group.hours[0] : null
        });
    });
    // Sorting:
    if (grouping === 'hour') {
        chartData.sort((a, b) => {
            if (a.date === b.date) return a.hour - b.hour;
            return new Date(a.date) - new Date(b.date);
        });
    } else {
        chartData.sort((a, b) => new Date(a.date) - new Date(b.date));
    }
    return chartData;
};

// Custom Tooltip: Display the label and occupancy percentage.
const CustomTooltip = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
            <div style={{backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc'}}>
                <p style={{margin: '0px'}}><strong>{`Time: ${label}`}</strong></p>
                <p style={{margin: '0px'}}>{`Play Time: ${data.occupancy.toFixed(2)}%`}</p>
                <p style={{margin: '0px'}}>{`Total Players: ${data.totalPlayers}`}</p>
            </div>
        );
    }
    return null;
};

// The unified component. Pass grouping as "hour" or "day".
const SimpleGameTimeOccupancyChart = ({rawData, grouping, workingStart, workingEnd}) => {
    const [showPlayersAxis, setShowPlayersAxis] = useState(false);
    const chartData = calculateSimpleOccupiedData(rawData, grouping, workingStart, workingEnd);
    const dataWithWeekend = chartData.map(d => ({...d, weekend: isWeekend(d.date)}));
    // Determine the maximum totalPlayers for the right Y axis domain.
    // const maxPlayersValue = dataWithWeekend.reduce((max, d) => Math.max(max, d.totalPlayers), 0);
    return (
        <div>
            <Typography variant="h5" align="center" gutterBottom>
                {grouping === 'hour'
                    ? 'Actual Play Time/Hour'
                    : 'Actual Play Time/Day'}
            </Typography>
            <FormControlLabel className="no-print"
                control={
                    <Switch
                        checked={showPlayersAxis}
                        onChange={() => setShowPlayersAxis(!showPlayersAxis)}
                        color="primary"
                    />
                }
                label="Show Players Axis"
            />
            <ResponsiveContainer width="100%" height={400}>
                <ComposedChart data={dataWithWeekend} margin={{top: 20, right: 50, left: 20, bottom: 5}}>
                    <CartesianGrid strokeDasharray="3 3"/>
                    <XAxis dataKey="label"/>
                    <YAxis yAxisId="left" tickFormatter={(val) => `${val.toFixed(1)}%`}/>
                    {showPlayersAxis && (
                        <YAxis
                            orientation="right"
                            yAxisId="right"
                        />
                    )}
                    <Tooltip content={<CustomTooltip/>}/>
                    <Bar yAxisId="left" dataKey="occupancy" isAnimationActive={false}>
                        {dataWithWeekend.map((entry, index) => {
                            const fillColor = entry.weekend ? '#0000FF' : '#008000';
                            return <Cell key={`cell-${index}`} fill={fillColor}/>;
                        })}
                    </Bar>
                    {showPlayersAxis && (
                        <Line
                            type="monotone"
                            dataKey="totalPlayers"
                            stroke="#ff7300"
                            strokeWidth={2}
                            dot={{r: 4}}
                            yAxisId="right"
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default SimpleGameTimeOccupancyChart;
